// ─── Écran distant ───
// Port de la console Telipso Assistance (Ecran.tsx) : tuiles JPEG décodées et
// dessinées dans l'ordre, accusé `recu` APRÈS le dessin (c'est lui qui autorise
// l'agent à envoyer l'image suivante), entrées en coordonnées de l'image.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Maximize2, Minimize2, ClipboardList, Download, Keyboard, MonitorOff, Lock, MessageSquare, TerminalSquare, PowerOff, WifiOff, FolderOpen, ListChecks,
} from 'lucide-react';
import { BINAIRE, lireCurseur, lireTrameImage } from './protocol.js';
import { useChannel } from './useChannel.js';
import { LADDER, initialAutoState, nextAutoSetting } from './autoQuality.js';
import { ClientFichiers, enregistrer, fmtTaille, joindre } from './fichiersClient.js';

// Combinaisons que le navigateur intercepterait : envoyées par menu.
const SPECIAL_KEYS = [
  { nom: 'Touche Windows', codes: ['MetaLeft'] },
  { nom: 'Alt + Tab', codes: ['AltLeft', 'Tab'] },
  { nom: 'Ctrl + Échap', codes: ['ControlLeft', 'Escape'] },
  { nom: 'Ctrl + Maj + Échap (gestionnaire des tâches)', codes: ['ControlLeft', 'ShiftLeft', 'Escape'] },
  { nom: 'Windows + R (Exécuter)', codes: ['MetaLeft', 'KeyR'] },
  { nom: 'Windows + E (Explorateur)', codes: ['MetaLeft', 'KeyE'] },
  { nom: 'Alt + F4', codes: ['AltLeft', 'F4'] },
  { nom: 'Impr. écran', codes: ['PrintScreen'] },
  // Réservées par le navigateur hors plein écran : aucune page ne peut les capter.
  { nom: 'Ctrl + W', codes: ['ControlLeft', 'KeyW'] },
  { nom: 'Ctrl + T', codes: ['ControlLeft', 'KeyT'] },
  { nom: 'Ctrl + N', codes: ['ControlLeft', 'KeyN'] },
  { nom: 'Ctrl + Tab', codes: ['ControlLeft', 'Tab'] },
  { nom: 'Ctrl + Maj + Tab', codes: ['ControlLeft', 'ShiftLeft', 'Tab'] },
  { nom: 'Windows + D (Bureau)', codes: ['MetaLeft', 'KeyD'] },
  { nom: 'Windows + T (barre des tâches)', codes: ['MetaLeft', 'KeyT'] },
  { nom: 'Windows + Tab', codes: ['MetaLeft', 'Tab'] },
];

// ─── Clavier capté au niveau de la fenêtre ───
// Une frappe part vers le poste où que soit le focus (bouton de la barre, case à
// cocher…), sauf dans un champ de saisie ou un panneau marqué `data-clavier-local`
// (discussion, terminal, fichiers). C'est ce qui retire au navigateur F1, Ctrl+F,
// Ctrl+P, F5, F12… : un preventDefault à la phase de capture les annule. Restent hors
// d'atteinte, sauf en plein écran (Keyboard Lock) : Ctrl+W/T/N/Tab, Alt+Tab, Windows.
function saisieLocale(el) {
  if (!el || el === document.body || el === document.documentElement) return false;
  if (el.closest?.('[data-clavier-local]')) return true;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT')
    return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file'].includes((el.type || '').toLowerCase());
  return false;
}

const btn =
  'inline-flex items-center gap-1.5 bg-rule border border-rule-3 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-muted cursor-pointer hover:bg-rule-2 disabled:opacity-50 disabled:cursor-not-allowed';
const btnOn = 'bg-blue-500/20 border-blue-500/50 text-blue-400';
const sel =
  'bg-card border border-rule-3 rounded-lg px-2 py-1.5 text-xs text-fg-2 cursor-pointer';

// Presse-papiers partagé : choix gardé par navigateur (même convention que mh_theme).
const CLE_PARTAGE = 'mh_remote_clipboard_shared';
const lirePartage = () => { try { return localStorage.getItem(CLE_PARTAGE) === '1'; } catch { return false; } };
const ecrirePartage = (v) => { try { localStorage.setItem(CLE_PARTAGE, v ? '1' : '0'); } catch { /* stockage indisponible */ } };
// Souris séparées : coché par défaut, choix gardé par navigateur.
const CLE_SOURIS = 'mh_remote_separate_mouse';
const lireSouris = () => { try { return localStorage.getItem(CLE_SOURIS) !== '0'; } catch { return true; } };
const ecrireSouris = (v) => { try { localStorage.setItem(CLE_SOURIS, v ? '1' : '0'); } catch { /* stockage indisponible */ } };
// L'API presse-papiers n'existe qu'en contexte sécurisé (HTTPS, ou 127.0.0.1 / localhost).
const pressepapiersNavigateur = () =>
  typeof window !== 'undefined' && window.isSecureContext && !!navigator.clipboard?.readText && !!navigator.clipboard?.writeText;

// ─── Images et fichiers dans le presse-papiers partagé ───
// Au-delà, le relais refuserait le message (10 Mo en base64 tiennent sous les 16 Mo d'un message texte).
const MAX_IMAGE = 10 * 1024 * 1024;
const NOM_CAPTURE = /^image\.(png|jpe?g|gif|bmp|webp)$/i;
const FICHIERS_PRETS_MS = 20000;

async function empreinte(buf) {
  const h = await crypto.subtle.digest('SHA-256', buf);
  return [...new Uint8Array(h)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function versBase64(buf) {
  const u = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
  return btoa(s);
}

function depuisBase64(b64) {
  const bin = atob(b64);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u;
}

/** Le poste n'accepte que du PNG : toute autre image passe par un canevas. */
async function versPng(blob) {
  if (blob.type === 'image/png') return blob;
  const bmp = await createImageBitmap(blob);
  const c = document.createElement('canvas');
  c.width = bmp.width;
  c.height = bmp.height;
  c.getContext('2d').drawImage(bmp, 0, 0);
  bmp.close();
  return new Promise((resoudre) => c.toBlob(resoudre, 'image/png'));
}

const imagesNavigateur = () => pressepapiersNavigateur() && typeof ClipboardItem !== 'undefined' && !!navigator.clipboard?.write;

/**
 * Instantané SYNCHRONE d'un événement paste : le DataTransfer se vide dès que le
 * gestionnaire rend la main, rien ne peut en être lu après un await.
 */
function extraireCollage(cd) {
  if (!cd) return null;
  const fichiers = [];
  let dossiers = 0;
  [...(cd.items || [])].forEach((it) => {
    if (it.kind !== 'file') return;
    if (it.webkitGetAsEntry?.()?.isDirectory) { dossiers += 1; return; }
    const f = it.getAsFile();
    if (f) fichiers.push(f);
  });
  if (fichiers.length === 0 && dossiers === 0) fichiers.push(...(cd.files || []));
  return { fichiers, dossiers, texte: cd.getData('text/plain') || '' };
}

// « Ajuster » : la plus grande taille qui tient dans la zone, sans dépasser la
// résolution réelle du poste (une image réduite à 50 % est réagrandie, pas au-delà).
function tailleAjustee(taille, boite, echelle) {
  if (!taille || !boite || !boite.l || !boite.h) return { maxWidth: '100%', maxHeight: '100%' };
  const r = Math.min(boite.l / taille.l, boite.h / taille.h, 100 / (echelle || 100));
  return { width: Math.floor(taille.l * r), height: Math.floor(taille.h * r) };
}

export function Screen({
  sessionId, capabilities, canTerminal, canFiles, canTasks, chatUnread, panel, onPanel, onEnd, onChannelClose, bureau,
}) {
  const racine = useRef(null);
  const toile = useRef(null);
  const curseur = useRef(null);
  const file = useRef(Promise.resolve());
  const touches = useRef(new Set());
  const dernierMouvement = useRef(0);
  const compteurs = useRef({ octets: 0, images: 0 });

  const [ecrans, setEcrans] = useState([]);
  const [ecran, setEcran] = useState(0);
  const [qualite, setQualite] = useState(70);
  const [ips, setIps] = useState(20);
  const [echelle, setEchelle] = useState(100);
  const [auto, setAuto] = useState(true);
  const [lent, setLent] = useState(false);
  const autoEtat = useRef(initialAutoState());
  const ackAt = useRef(0);
  const delais = useRef([]); // { at, ms } sur ~3 s
  const ipsRef = useRef(20);
  const [ajuster, setAjuster] = useState(true);
  const [taille, setTaille] = useState(null);
  // Zone d'affichage mesurée : « Ajuster » calcule la taille de la toile au pixel près.
  // Un max-height en `calc(100vh - 90px)` supposait une barre d'outils d'une ligne ;
  // repliée sur deux, l'image débordait et une barre de défilement apparaissait.
  const zone = useRef(null);
  const [boite, setBoite] = useState(null);
  const [mesure, setMesure] = useState({ debit: 0, images: 0 });
  const [avertissement, setAvertissement] = useState('');
  const [pressePapiers, setPressePapiers] = useState(null);
  const [etatPoste, setEtatPoste] = useState({ confidentialite: false, blocage: false, bureauSecurise: false, imageVide: false, sessionDistante: false });
  const [pleinEcranActif, setPleinEcranActif] = useState(false);
  const [partage, setPartage] = useState(lirePartage);
  const partageRef = useRef(partage);
  const [sourisSeparee, setSourisSeparee] = useState(lireSouris);
  const sourisRef = useRef(sourisSeparee);
  const dernierTexte = useRef(null);   // dernier texte échangé, dans un sens ou l'autre
  const ecritureEnAttente = useRef(null);
  const porte = useRef(null);          // file des entrées pendant une lecture du presse-papiers
  // Images et fichiers : empreintes des images déjà échangées (pas de renvoi), canal « fichiers »
  // propre au presse-papiers (ouvert à la première utilisation), état des bandeaux.
  const empreintes = useRef([]);
  const [fichiersActif, setFichiersActif] = useState(false);
  const [fichiersCle, setFichiersCle] = useState(0);
  const pretFichiers = useRef(null);
  const canalFichiersRef = useRef(null);
  const clientFichiersRef = useRef(null);
  if (!clientFichiersRef.current) clientFichiersRef.current = new ClientFichiers(() => canalFichiersRef.current);
  const annulerEnvoi = useRef(false);
  const [envoi, setEnvoi] = useState(null);                 // { n, fait, total } : fichiers collés vers le poste
  const [fichiersPoste, setFichiersPoste] = useState(null); // { liste } : fichiers copiés sur le poste
  const [telechargement, setTelechargement] = useState(null); // { fait, total }

  const cap = (c) => capabilities.includes(c);
  const partageDispo = cap('presse_papiers_partage');
  const sourisDispo = cap('souris_separee');
  const partageNavigateur = pressepapiersNavigateur();
  const mediasDispo = partageDispo && cap('presse_papiers_fichiers');

  const dessiner = useCallback(async (trame) => {
    const c = toile.current;
    if (!c) return;
    if (c.width !== trame.largeur || c.height !== trame.hauteur) {
      c.width = trame.largeur;
      c.height = trame.hauteur;
      setTaille({ l: trame.largeur, h: trame.hauteur });
    }
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const images = await Promise.all(
      trame.tuiles.map((t) => createImageBitmap(new Blob([t.jpeg], { type: 'image/jpeg' }))),
    );
    images.forEach((image, i) => {
      ctx.drawImage(image, trame.tuiles[i].x, trame.tuiles[i].y);
      image.close();
    });
    compteurs.current.images++;
  }, []);

  const placerCurseur = useCallback((x, y) => {
    const c = toile.current;
    const div = curseur.current;
    if (!c || !div || !c.width) return;
    div.style.display = x < 0 ? 'none' : 'block';
    div.style.left = `${(x / c.width) * 100}%`;
    div.style.top = `${(y / c.height) * 100}%`;
  }, []);

  const { etat, raison, envoyer } = useChannel(
    sessionId,
    'ecran',
    (e) => {
      if (typeof e.data === 'string') {
        let m;
        try { m = JSON.parse(e.data); } catch { return; }
        if (m.t === 'ecrans') {
          setEcrans(m.liste || []);
          setEcran(m.actif || 0);
        } else if (m.t === 'presse_papiers') {
          // `auto` = copie faite sur le poste, poussée par le presse-papiers partagé.
          if (m.auto) { if (partageRef.current) ecrireLocal(m.texte || ''); }
          else setPressePapiers(m.texte || '');
        }
        else if (m.t === 'presse_papiers_image') {
          if (m.auto && m.png && partageRef.current && mediasDispo) ecrireLocalImage(m.png);
        } else if (m.t === 'presse_papiers_fichiers') {
          if (m.auto && partageRef.current && mediasDispo) setFichiersPoste({ liste: m.fichiers || [] });
        }
        else if (m.t === 'erreur') setAvertissement(m.message || 'Erreur signalée par le poste.');
        else if (m.t === 'etat')
          setEtatPoste({
            confidentialite: !!m.confidentialite, blocage: !!m.blocage, bureauSecurise: !!m.bureauSecurise,
            imageVide: !!m.imageVide, sessionDistante: !!m.sessionDistante,
          });
        return;
      }
      const tampon = e.data;
      compteurs.current.octets += tampon.byteLength;
      const genre = new DataView(tampon).getUint8(0);
      if (genre === BINAIRE.IMAGE) {
        const trame = lireTrameImage(tampon);
        const maintenant = performance.now();
        if (ackAt.current) {
          // On retire l'attente normale due au plafond d'images par seconde.
          const ms = Math.max(0, maintenant - ackAt.current - 1000 / ipsRef.current);
          delais.current.push({ at: maintenant, ms });
        }
        // Dessin dans l'ordre ; l'accusé autorise l'agent à envoyer la suivante.
        file.current = file.current
          .then(() => dessiner(trame))
          .catch(() => {})
          .then(() => {
            ackAt.current = performance.now();
            envoyer({ t: 'recu', seq: trame.seq });
          });
      } else if (genre === BINAIRE.CURSEUR) {
        const p = lireCurseur(tampon);
        placerCurseur(p.x, p.y);
      }
    },
    (send) => {
      send({ t: 'demarrer', qualite, ecran: 0, imagesParSeconde: ips, echelle });
      if (partageRef.current) send({ t: 'presse_papiers_partage', actif: true });
      if (sourisRef.current && cap('souris_separee')) send({ t: 'souris_separee', actif: true });
    },
    // `bureau` = session Windows affichée : l'agent qui suit la console en change (cf. bureau_change), on rouvre.
    { onClose: onChannelClose, restartKey: bureau },
  );

  // Canal « fichiers » du presse-papiers : ouvert à la demande, séparé de celui du panneau Fichiers.
  const canalFichiers = useChannel(
    sessionId,
    'fichiers',
    (e) => clientFichiersRef.current.traiter(e),
    () => {
      const p = pretFichiers.current;
      if (p) { p.ouvert = true; p.resoudre(); }
    },
    {
      enabled: fichiersActif,
      restartKey: fichiersCle,
      onClose: () => {
        const p = pretFichiers.current;
        if (p) { p.ferme = true; p.rejeter(new Error('Canal fichiers fermé')); }
        clientFichiersRef.current.rejeterTout('Canal fichiers fermé');
      },
    },
  );
  canalFichiersRef.current = canalFichiers;

  function ouvrirFichiers() {
    const p = pretFichiers.current;
    if (p && !p.ferme) return p.promesse;
    const nouveau = { ouvert: false, ferme: false };
    nouveau.promesse = new Promise((resoudre, rejeter) => {
      nouveau.resoudre = resoudre;
      nouveau.rejeter = rejeter;
      setTimeout(() => {
        if (nouveau.ouvert || nouveau.ferme) return;
        nouveau.ferme = true; // la prochaine demande rouvre le canal
        rejeter(new Error('le canal fichiers du poste ne s’ouvre pas'));
      }, FICHIERS_PRETS_MS);
    });
    nouveau.promesse.catch(() => {});
    pretFichiers.current = nouveau;
    setFichiersActif(true);
    setFichiersCle((k) => k + 1);
    return nouveau.promesse;
  }

  useEffect(() => {
    const el = zone.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setBoite({ l: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const minuterie = setInterval(() => {
      setMesure({ debit: compteurs.current.octets / 1024, images: compteurs.current.images });
      compteurs.current = { octets: 0, images: 0 };
    }, 1000);
    return () => clearInterval(minuterie);
  }, []);

  // La molette doit pouvoir appeler preventDefault : écouteur non passif.
  useEffect(() => {
    const c = toile.current;
    if (!c) return undefined;
    const molette = (e) => {
      e.preventDefault();
      const crans = Math.max(1, Math.round(Math.abs(e.deltaY) / 100));
      entreeRef.current({ t: 'molette', dy: Math.sign(e.deltaY) * crans });
    };
    c.addEventListener('wheel', molette, { passive: false });
    return () => c.removeEventListener('wheel', molette);
  }, []);

  useEffect(() => {
    const maj = () => setPleinEcranActif(document.fullscreenElement === racine.current);
    document.addEventListener('fullscreenchange', maj);
    return () => {
      document.removeEventListener('fullscreenchange', maj);
      try { navigator.keyboard?.unlock?.(); } catch { /* indisponible */ }
    };
  }, []);

  const regler = (r) => envoyer({ t: 'reglages', ...r });

  // ─── Presse-papiers partagé ───
  // Poste ▸ ici : l'agent pousse chaque copie (`presse_papiers` + `auto`). Ici ▸ poste :
  // le presse-papiers local est relu quand la fenêtre reprend le focus (le technicien
  // revient d'une autre application où il a copié), et posé sur le poste s'il a changé.
  // Les entrées attendent la fin de cette lecture : un Ctrl+V tapé aussitôt doit coller
  // le NOUVEAU texte.
  function enFile(tache) {
    const suivante = (porte.current || Promise.resolve()).then(tache).catch(() => {});
    porte.current = suivante;
    suivante.then(() => { if (porte.current === suivante) porte.current = null; });
  }
  const entree = (m) => { if (porte.current) enFile(() => envoyer(m)); else envoyer(m); };
  const entreeRef = useRef(entree);
  entreeRef.current = entree;

  function ecrireLocal(texte) {
    dernierTexte.current = texte;
    // writeText exige que le document ait le focus : sinon, au prochain retour.
    navigator.clipboard?.writeText(texte).then(
      () => { ecritureEnAttente.current = null; },
      () => { ecritureEnAttente.current = texte; },
    );
  }

  const retenir = (h) => { empreintes.current = [...empreintes.current.filter((x) => x !== h), h].slice(-8); };

  async function ecrireImageLocale(blob) {
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
  }

  // Image copiée sur le poste : posée ici, en attente du focus si le navigateur refuse.
  async function ecrireLocalImage(b64) {
    if (!imagesNavigateur()) return;
    let octets;
    try { octets = depuisBase64(b64); } catch { return; }
    const blob = new Blob([octets], { type: 'image/png' });
    try { retenir(await empreinte(octets)); } catch { /* contexte non sécurisé : pas d'empreinte */ }
    try {
      await ecrireImageLocale(blob);
      ecritureEnAttente.current = null;
      // Le navigateur peut ré-encoder le PNG : on retient aussi l'empreinte de ce qu'il rend,
      // sinon le retour du focus renverrait l'image au poste.
      try {
        for (const it of await navigator.clipboard.read())
          if (it.types.includes('image/png')) retenir(await empreinte(await (await it.getType('image/png')).arrayBuffer()));
      } catch { /* relecture refusée : au pire un renvoi identique */ }
    } catch {
      ecritureEnAttente.current = { image: blob };
    }
  }

  // Envoie une image au poste (PNG, 10 Mo au plus). Rend false si elle ne part pas.
  async function envoyerImage(fichier) {
    const png = await versPng(fichier);
    if (!png || png.size > MAX_IMAGE) {
      setAvertissement('Image trop volumineuse pour le presse-papiers partagé (10 Mo au plus).');
      return false;
    }
    const buf = await png.arrayBuffer();
    retenir(await empreinte(buf));
    envoyer({ t: 'presse_papiers_image', png: versBase64(buf) });
    return true;
  }

  // Texte lu localement : envoyé seulement s'il diffère du dernier texte échangé.
  function envoyerTexte(texte) {
    if (!texte || texte === dernierTexte.current) return;
    dernierTexte.current = texte;
    envoyer({ t: 'presse_papiers_envoyer', texte });
  }

  // Fichiers collés : déposés dans un dossier de transit du poste, puis posés dans son presse-papiers.
  async function envoyerFichiers(fichiers) {
    if (!canFiles) {
      setAvertissement('Coller des fichiers exige la permission « exécuter des commandes » et la capacité Fichiers du poste.');
      return false;
    }
    const total = fichiers.reduce((n, f) => n + f.size, 0);
    let finis = 0;
    let courant = 0;
    const maj = () => setEnvoi({ n: fichiers.length, fait: finis + courant, total });
    annulerEnvoi.current = false;
    maj();
    try {
      await ouvrirFichiers();
      const client = clientFichiersRef.current;
      const { chemin } = await client.demander({ t: 'dossier_presse_papiers' });
      const chemins = [];
      for (const f of fichiers) {
        courant = 0;
        await client.televerser(f, chemin, { progres: (_id, fait) => { courant = fait; maj(); }, annule: () => annulerEnvoi.current });
        finis += f.size;
        courant = 0;
        maj();
        chemins.push(joindre(chemin, f.name));
      }
      envoyer({ t: 'presse_papiers_fichiers', chemins });
      return true;
    } catch (err) {
      setAvertissement('Envoi des fichiers vers le poste impossible : ' + (err?.message || err));
      return false;
    } finally {
      setEnvoi(null);
    }
  }

  // Ce que Ctrl+V vient de capter : image de capture, fichiers ou texte. Rend false si la frappe
  // ne doit pas partir (le poste collerait un contenu périmé), 'differe' si elle partira plus tard.
  async function collerDonnees({ fichiers, dossiers, texte }, majInser) {
    if (dossiers > 0) setAvertissement('Les dossiers ne se collent pas sur le poste : utilisez le panneau Fichiers.');
    if (fichiers.length === 1 && dossiers === 0 && fichiers[0].type.startsWith('image/') && NOM_CAPTURE.test(fichiers[0].name))
      return envoyerImage(fichiers[0]);
    if (fichiers.length > 0) {
      // Un envoi de fichiers peut durer des minutes : il ne bloque PAS les entrées (la
      // visionneuse resterait figée). À la fin, la combinaison complète part d'elle-même,
      // les touches du technicien étant relâchées depuis longtemps.
      envoyerFichiers(fichiers).then((ok) => {
        if (!ok) return;
        const codes = majInser ? ['ShiftLeft', 'Insert'] : ['ControlLeft', 'KeyV'];
        codes.forEach((code) => entreeRef.current({ t: 'touche', action: 'appuyer', code }));
        [...codes].reverse().forEach((code) => entreeRef.current({ t: 'touche', action: 'relacher', code }));
      });
      return 'differe';
    }
    if (dossiers > 0) return false;
    envoyerTexte(texte);
    return true;
  }

  // Ctrl+V / Maj+Inser : la frappe n'est PAS annulée (sans cela le navigateur ne déclenche pas
  // `paste`) et part après l'envoi du contenu. Sans événement paste sous 150 ms, elle part telle quelle.
  function collage(e) {
    if (!partageRef.current || !mediasDispo || !partageNavigateur || e.repeat || e.altKey || e.metaKey) return false;
    const ctrlV = e.ctrlKey && !e.shiftKey && e.code === 'KeyV';
    const majInser = e.shiftKey && !e.ctrlKey && e.code === 'Insert';
    if (!ctrlV && !majInser) return false;
    touches.current.add(e.code);
    const { code, key } = e;
    let delai;
    const instantane = new Promise((resoudre) => {
      const fin = (donnees) => {
        clearTimeout(delai);
        document.removeEventListener('paste', surCollage, true);
        resoudre(donnees);
      };
      const surCollage = (ev) => { ev.preventDefault(); ev.stopPropagation(); fin(extraireCollage(ev.clipboardData)); };
      document.addEventListener('paste', surCollage, true);
      delai = setTimeout(() => fin(null), 150);
    });
    enFile(async () => {
      const donnees = await instantane;
      let partir = true;
      if (donnees) {
        try { partir = (await collerDonnees(donnees, majInser)) === true; } catch (err) {
          partir = false;
          setAvertissement('Collage impossible : ' + (err?.message || err));
        }
      }
      if (partir) envoyer({ t: 'touche', action: 'appuyer', code, cle: key });
    });
    return true;
  }

  // Fichiers copiés sur le poste : un seul bouton les télécharge par le canal « fichiers ».
  async function telechargerCopies() {
    const fichiers = (fichiersPoste?.liste || []).filter((f) => !f.dossier);
    const total = fichiers.reduce((n, f) => n + (f.taille || 0), 0);
    let finis = 0;
    setTelechargement({ fait: 0, total });
    try {
      await ouvrirFichiers();
      for (const f of fichiers) {
        const { nom, morceaux } = await clientFichiersRef.current.telecharger(f.chemin, {
          progres: (_id, fait) => setTelechargement({ fait: finis + fait, total }),
        });
        enregistrer(nom, morceaux);
        finis += f.taille || 0;
      }
    } catch (err) {
      setAvertissement('Téléchargement des fichiers du poste impossible : ' + (err?.message || err));
    } finally {
      setTelechargement(null);
    }
  }

  const synchroniser = useCallback(() => {
    if (!partageRef.current || !pressepapiersNavigateur()) return;
    enFile(async () => {
      if (ecritureEnAttente.current !== null) {
        const t = ecritureEnAttente.current;
        try {
          if (typeof t === 'string') await navigator.clipboard.writeText(t);
          else await ecrireImageLocale(t.image);
          ecritureEnAttente.current = null;
        } catch { /* toujours pas le focus */ }
        return; // le poste a copié en dernier : c'est lui qui fait foi
      }
      // Borné : une invite d'autorisation laissée sans réponse ne doit pas geler le clavier.
      const borne = (p) => Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error('delai')), 4000))]);
      const refuse = (err) => {
        if (err?.name === 'NotAllowedError')
          setAvertissement('Le navigateur refuse la lecture du presse-papiers : autorisez-la pour ce site (icône à gauche de l’adresse).');
      };
      let texte;
      if (mediasDispo && navigator.clipboard.read) {
        try {
          const items = await borne(navigator.clipboard.read());
          for (const it of items) {
            // Une image SANS texte : une capture. Avec texte (Excel, Word) c'est le texte qu'on veut.
            if (it.types.includes('image/png') && !it.types.includes('text/plain')) {
              const blob = await it.getType('image/png');
              if (blob.size > MAX_IMAGE) { setAvertissement('Image trop volumineuse pour le presse-papiers partagé (10 Mo au plus).'); return; }
              const buf = await blob.arrayBuffer();
              const h = await empreinte(buf);
              if (empreintes.current.includes(h)) return;
              retenir(h);
              envoyer({ t: 'presse_papiers_image', png: versBase64(buf) });
              return;
            }
            if (it.types.includes('text/plain')) { texte = await (await it.getType('text/plain')).text(); break; }
          }
          envoyerTexte(texte);
          return;
        } catch (err) {
          if (err?.name === 'NotAllowedError') { refuse(err); return; }
          // autre échec (type non géré) : repli sur la lecture du texte seul
        }
      }
      try {
        texte = await borne(navigator.clipboard.readText());
      } catch (err) {
        refuse(err);
        return;
      }
      envoyerTexte(texte);
    });
  }, [envoyer, mediasDispo, canFiles]);

  useEffect(() => {
    if (!partage) return undefined;
    const surFocus = () => synchroniser();
    const surVisibilite = () => { if (document.visibilityState === 'visible') synchroniser(); };
    // Une copie faite DANS cette page (discussion, terminal) ne change pas le focus.
    const surCopie = () => setTimeout(synchroniser, 0);
    window.addEventListener('focus', surFocus);
    document.addEventListener('visibilitychange', surVisibilite);
    document.addEventListener('copy', surCopie);
    return () => {
      window.removeEventListener('focus', surFocus);
      document.removeEventListener('visibilitychange', surVisibilite);
      document.removeEventListener('copy', surCopie);
    };
  }, [partage, synchroniser]);

  function basculerSouris(v) {
    setSourisSeparee(v);
    sourisRef.current = v;
    ecrireSouris(v);
    envoyer({ t: 'souris_separee', actif: v });
  }

  function basculerPartage(v) {
    setPartage(v);
    partageRef.current = v;
    ecrirePartage(v);
    dernierTexte.current = null;
    empreintes.current = [];
    ecritureEnAttente.current = null;
    if (!v) setFichiersPoste(null);
    envoyer({ t: 'presse_papiers_partage', actif: v });
    // Clic = geste de l'usager : c'est le bon moment pour l'invite d'autorisation.
    if (v) synchroniser();
  }

  // Boucle du mode Auto : une évaluation par seconde sur la fenêtre glissante de 3 s.
  useEffect(() => {
    if (!auto) return undefined;
    const iv = setInterval(() => {
      const now = performance.now();
      delais.current = delais.current.filter((d) => now - d.at <= 3000);
      const l = delais.current;
      const avgDelayMs = l.length ? l.reduce((a, d) => a + d.ms, 0) / l.length : null;
      const r = nextAutoSetting(autoEtat.current, { now, avgDelayMs });
      autoEtat.current = r.state;
      setLent(r.state.level > 0);
      if (r.change) {
        setQualite(r.change.qualite);
        setEchelle(r.change.echelle);
        envoyer({ t: 'reglages', qualite: r.change.qualite, echelle: r.change.echelle });
        delais.current = [];
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [auto, envoyer]);

  // Toucher à un réglage manuel reprend la main ; recocher « Auto » repart du cran 0.
  const manuel = (r) => {
    setAuto(false);
    setLent(false);
    regler(r);
  };
  function basculerAuto(v) {
    setAuto(v);
    if (!v) return;
    autoEtat.current = initialAutoState();
    delais.current = [];
    setQualite(LADDER[0].qualite);
    setEchelle(LADDER[0].echelle);
    regler({ qualite: LADDER[0].qualite, echelle: LADDER[0].echelle });
  }

  function coordonnees(e) {
    const c = toile.current;
    const r = c.getBoundingClientRect();
    return {
      x: Math.round(((e.clientX - r.left) * c.width) / r.width),
      y: Math.round(((e.clientY - r.top) * c.height) / r.height),
    };
  }

  function souris(action, e) {
    if (action === 'deplacer') {
      const t = performance.now();
      if (t - dernierMouvement.current < 30) return;
      dernierMouvement.current = t;
    } else {
      e.preventDefault();
      toile.current?.focus();
    }
    entree({ t: 'souris', action, bouton: e.button, ...coordonnees(e) });
  }

  function touche(action, e) {
    e.preventDefault();
    if (!e.code) return;
    if (action === 'appuyer') touches.current.add(e.code);
    else touches.current.delete(e.code);
    entree({ t: 'touche', action, code: e.code, cle: e.key });
  }

  // Une touche restée « enfoncée » côté poste si la fenêtre perd le focus (Alt+Tab
  // local, autre application) : on la relâche, sinon Alt resterait enfoncé là-bas.
  function relacherTouches() {
    touches.current.forEach((code) => entree({ t: 'touche', action: 'relacher', code }));
    touches.current.clear();
  }

  function combinaison(codes) {
    codes.forEach((code) => entree({ t: 'touche', action: 'appuyer', code }));
    [...codes].reverse().forEach((code) => entree({ t: 'touche', action: 'relacher', code }));
    toile.current?.focus();
  }

  const clavier = useRef(null);
  clavier.current = { touche, relacherTouches, collage, ouvert: etat === 'ouvert' };
  useEffect(() => {
    const bas = (e) => {
      const k = clavier.current;
      if (!k.ouvert || e.isComposing || saisieLocale(e.target)) return;
      // Ctrl+C sur un texte sélectionné ailleurs que sur l'écran (discussion…) : copie locale.
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyC' && e.target !== toile.current && String(window.getSelection?.() || ''))
        return;
      e.stopPropagation();
      if (k.collage(e)) {
        if (document.activeElement !== toile.current) toile.current?.focus({ preventScroll: true });
        return;
      }
      k.touche('appuyer', e);
      if (document.activeElement !== toile.current) toile.current?.focus({ preventScroll: true });
    };
    const haut = (e) => {
      if (!touches.current.has(e.code)) return;
      e.stopPropagation();
      clavier.current.touche('relacher', e);
    };
    const perte = () => clavier.current.relacherTouches();
    const visibilite = () => { if (document.visibilityState === 'hidden') perte(); };
    window.addEventListener('keydown', bas, true);
    window.addEventListener('keyup', haut, true);
    window.addEventListener('blur', perte);
    document.addEventListener('visibilitychange', visibilite);
    return () => {
      window.removeEventListener('keydown', bas, true);
      window.removeEventListener('keyup', haut, true);
      window.removeEventListener('blur', perte);
      document.removeEventListener('visibilitychange', visibilite);
    };
  }, []);

  async function pleinEcran() {
    const el = racine.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
        // Chrome/Edge : capture aussi Échap, Alt+Tab, etc.
        navigator.keyboard?.lock?.().catch(() => {});
        toile.current?.focus();
      }
    } catch { /* refusé par le navigateur */ }
  }

  return (
    <div ref={racine} className="flex flex-col flex-1 min-w-0 min-h-0 bg-app">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-rule bg-sidebar">
        {ecrans.length > 1 && (
          <select
            className={sel}
            value={ecran}
            onChange={(e) => { setEcran(Number(e.target.value)); regler({ ecran: Number(e.target.value) }); }}
          >
            {ecrans.map((d) => (
              <option key={d.index} value={d.index}>
                Écran {d.index + 1} ({d.largeur}×{d.hauteur}{d.principal ? ', principal' : ''})
              </option>
            ))}
          </select>
        )}
        <label className="inline-flex items-center gap-1 text-xs text-muted cursor-pointer" title="Adapte la qualité et l’échelle à la qualité de la connexion">
          <input type="checkbox" checked={auto} onChange={(e) => basculerAuto(e.target.checked)} /> Auto
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-muted" title="Qualité JPEG">
          Qualité
          <input
            type="range" min={10} max={95} step={5} value={qualite}
            onChange={(e) => { const v = Number(e.target.value); setQualite(v); manuel({ qualite: v }); }}
            style={{ width: 90 }}
          />
          <span className="font-mono w-5">{qualite}</span>
        </label>
        <select
          className={sel}
          value={echelle}
          title="Échelle de l’image"
          onChange={(e) => { const v = Number(e.target.value); setEchelle(v); manuel({ echelle: v }); }}
        >
          {[100, 75, 50, 33].map((v) => (
            <option key={v} value={v}>Échelle {v} %</option>
          ))}
        </select>
        <select
          className={sel}
          value={ips}
          title="Images par seconde au plus"
          onChange={(e) => { const v = Number(e.target.value); setIps(v); ipsRef.current = v; manuel({ imagesParSeconde: v }); }}
        >
          {[5, 8, 12, 20, 30].map((v) => (
            <option key={v} value={v}>{v} im/s</option>
          ))}
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-muted cursor-pointer">
          <input type="checkbox" checked={ajuster} onChange={(e) => setAjuster(e.target.checked)} /> Ajuster
        </label>
        <select
          className={sel}
          value=""
          onChange={(e) => {
            const choix = SPECIAL_KEYS[Number(e.target.value)];
            if (choix) combinaison(choix.codes);
          }}
        >
          <option value="">Touches spéciales…</option>
          {SPECIAL_KEYS.map((t, i) => (
            <option key={t.nom} value={i}>{t.nom}</option>
          ))}
        </select>
        {cap('cad') && (
          <button
            className={btn}
            onClick={() => envoyer({ t: 'cad' })}
            title="Ctrl+Alt+Suppr : écran de sécurité, ouverture de session"
          >
            <Keyboard size={14} strokeWidth={1.75} /> Ctrl+Alt+Suppr
          </button>
        )}
        {cap('confidentialite') && (
          <button
            className={`${btn} ${etatPoste.confidentialite ? btnOn : ''}`}
            onClick={() => envoyer({ t: 'confidentialite', actif: !etatPoste.confidentialite })}
            title="Écran noir chez l’usager pendant l’intervention"
          >
            <MonitorOff size={14} strokeWidth={1.75} /> Écran noir
          </button>
        )}
        {cap('blocage') && (
          <button
            className={`${btn} ${etatPoste.blocage ? btnOn : ''}`}
            onClick={() => envoyer({ t: 'blocage', actif: !etatPoste.blocage })}
            title="Bloquer le clavier et la souris de l’usager"
          >
            <Lock size={14} strokeWidth={1.75} /> Bloquer l’usager
          </button>
        )}
        <button className={`${btn} ${pressePapiers !== null ? btnOn : ''}`} onClick={() => setPressePapiers(pressePapiers === null ? '' : null)}>
          <ClipboardList size={14} strokeWidth={1.75} /> Presse-papiers
        </button>
        {partageDispo && (
          <label
            className={`inline-flex items-center gap-1 text-xs text-muted ${partageNavigateur ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
            title={
              partageNavigateur
                ? 'Texte copié ici collable sur le poste, et texte copié sur le poste collable ici'
                : 'Indisponible : le navigateur n’autorise le presse-papiers qu’en HTTPS'
            }
          >
            <input
              type="checkbox"
              checked={partage && partageNavigateur}
              disabled={!partageNavigateur}
              onChange={(e) => basculerPartage(e.target.checked)}
            />
            Presse-papiers partagé
          </label>
        )}
        {sourisDispo && (
          <label
            className="inline-flex items-center gap-1 text-xs text-muted cursor-pointer"
            title="Vos déplacements ne bougent pas le pointeur de l’usager ; clics, glisser et molette agissent à votre position, puis son pointeur revient. À décocher pour ce qui dépend du survol (infobulles, menus au survol)."
          >
            <input
              type="checkbox"
              checked={sourisSeparee}
              onChange={(e) => basculerSouris(e.target.checked)}
            />
            Souris séparées
          </label>
        )}
        <button
          className={btn}
          onClick={pleinEcran}
          title="Plein écran : capte aussi Alt+Tab, la touche Windows et Ctrl+W/T/N (maintenir Échap pour sortir)"
        >
          {pleinEcranActif ? <Minimize2 size={14} strokeWidth={1.75} /> : <Maximize2 size={14} strokeWidth={1.75} />} Plein écran
        </button>
        <button className={`${btn} ${panel === 'chat' ? btnOn : ''} relative`} onClick={() => onPanel('chat')}>
          <MessageSquare size={14} strokeWidth={1.75} /> Discussion
          {chatUnread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 bg-crit text-white rounded-full text-[10px] min-w-[16px] h-4 px-1 flex items-center justify-center">
              {chatUnread}
            </span>
          )}
        </button>
        {canTerminal && (
          <button className={`${btn} ${panel === 'terminal' ? btnOn : ''}`} onClick={() => onPanel('terminal')}>
            <TerminalSquare size={14} strokeWidth={1.75} /> Terminal
          </button>
        )}
        {canFiles && (
          <button className={`${btn} ${panel === 'fichiers' ? btnOn : ''}`} onClick={() => onPanel('fichiers')}>
            <FolderOpen size={14} strokeWidth={1.75} /> Fichiers
          </button>
        )}
        {canTasks && (
          <button className={`${btn} ${panel === 'taches' ? btnOn : ''}`} onClick={() => onPanel('taches')}>
            <ListChecks size={14} strokeWidth={1.75} /> Tâches
          </button>
        )}
        <div className="flex-1" />
        <span className="text-[11px] text-muted-2 flex items-center gap-2">
          {etat === 'connexion' && 'Connexion au poste…'}
          {etat === 'ouvert' && etatPoste.bureauSecurise && (
            <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 font-semibold">Bureau sécurisé</span>
          )}
          {etat === 'ouvert' && auto && lent && (
            <span className="inline-flex items-center gap-1 text-amber-300" title="Connexion lente : le mode Auto a réduit la qualité ou l’échelle">
              <WifiOff size={13} strokeWidth={1.75} /> Connexion lente
            </span>
          )}
          {etat === 'ouvert' && taille && `${taille.l}×${taille.h}, éch. ${echelle} %, qualité ${qualite}, ${mesure.images} im/s, ${Math.round(mesure.debit)} Ko/s`}
          {etat === 'ferme' && raison}
        </span>
        <button
          className="inline-flex items-center gap-1.5 bg-crit/12 border border-crit/35 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-red-300 cursor-pointer hover:bg-crit/20"
          onClick={onEnd}
          title="Terminer la session pour tous les techniciens"
        >
          <PowerOff size={14} strokeWidth={1.75} /> Terminer
        </button>
      </div>
      {etat === 'ouvert' && etatPoste.imageVide && (
        // Rien à capturer : Windows ne dessine plus la session (cf. CaptureWorker.BlankImage).
        <div className="px-3 py-1.5 text-xs bg-amber-500/10 text-amber-300 border-b border-rule flex flex-wrap items-center gap-2">
          <span>
            {etatPoste.sessionDistante
              ? 'Rien à afficher : la fenêtre Bureau à distance de l’usager est probablement réduite (Windows suspend alors l’affichage de sa session). Demandez-lui de la restaurer.'
              : 'Rien à afficher : l’écran du poste est noir (écran en veille ou éteint). Bougez la souris ou appuyez sur une touche pour le réveiller.'}
          </span>
          {etatPoste.sessionDistante && (
            <button className="bg-transparent border-none text-amber-200 underline cursor-pointer p-0 text-xs" onClick={() => onPanel('chat')}>
              Écrire à l’usager
            </button>
          )}
        </div>
      )}
      {(etatPoste.confidentialite || etatPoste.blocage) && (
        <div className="px-3 py-1 text-[11px] bg-amber-500/10 text-amber-300 border-b border-rule">
          {[etatPoste.confidentialite && 'Écran noir chez l’usager', etatPoste.blocage && 'Clavier et souris de l’usager bloqués']
            .filter(Boolean).join(' · ')}
        </div>
      )}
      {avertissement && (
        <div className="px-3 py-1.5 text-xs bg-crit/10 text-red-300 border-b border-rule flex justify-between">
          <span>{avertissement}</span>
          <button className="bg-transparent border-none text-red-300 cursor-pointer" onClick={() => setAvertissement('')}>×</button>
        </div>
      )}
      {envoi && (
        <div className="px-3 py-1.5 text-xs bg-card text-fg-2 border-b border-rule flex items-center gap-3">
          <span>
            Envoi de {envoi.n} fichier{envoi.n > 1 ? 's' : ''} vers le poste… {envoi.total ? Math.min(100, Math.round((envoi.fait / envoi.total) * 100)) : 100} %
            <span className="text-muted-2"> ({fmtTaille(envoi.fait)} / {fmtTaille(envoi.total)}) · le clavier attend la fin de l’envoi</span>
          </span>
          <button className={btn} onClick={() => { annulerEnvoi.current = true; }}>Annuler</button>
        </div>
      )}
      {fichiersPoste && (() => {
        const fichiers = fichiersPoste.liste.filter((f) => !f.dossier);
        const dossiers = fichiersPoste.liste.filter((f) => f.dossier);
        return (
          <div className="px-3 py-1.5 text-xs bg-card text-fg-2 border-b border-rule flex flex-wrap items-center gap-3">
            <span>
              {fichiers.length} fichier{fichiers.length > 1 ? 's' : ''} copié{fichiers.length > 1 ? 's' : ''} sur le poste
              {dossiers.length > 0 && (
                <span className="text-muted-2">
                  {' '}· {dossiers.length} dossier{dossiers.length > 1 ? 's' : ''} non téléchargeable{dossiers.length > 1 ? 's' : ''} ({dossiers.map((d) => d.nom).join(', ')}) : utilisez le panneau Fichiers
                </span>
              )}
              {!canFiles && <span className="text-amber-300"> · téléchargement indisponible (permission « exécuter des commandes » ou capacité Fichiers manquante)</span>}
            </span>
            {canFiles && fichiers.length > 0 && !telechargement && (
              <button className={btn} onClick={telechargerCopies}><Download size={14} strokeWidth={1.75} /> Télécharger</button>
            )}
            {telechargement && (
              <span className="text-muted-2">
                Téléchargement… {telechargement.total ? Math.min(100, Math.round((telechargement.fait / telechargement.total) * 100)) : 100} %
              </span>
            )}
            <button className="bg-transparent border-none text-muted cursor-pointer" onClick={() => setFichiersPoste(null)} aria-label="Fermer">×</button>
          </div>
        );
      })()}
      {pressePapiers !== null && (
        <div className="px-3 py-2 border-b border-rule bg-card flex flex-col gap-2">
          <textarea
            className="w-full bg-app border border-rule-3 rounded-lg p-2 text-xs text-fg font-mono"
            rows={3}
            value={pressePapiers}
            onChange={(e) => setPressePapiers(e.target.value)}
            placeholder="Texte à transmettre au poste, ou lu depuis le poste"
          />
          <div className="flex flex-wrap gap-2">
            <button className={btn} disabled={!pressePapiers} onClick={() => { envoyer({ t: 'texte', texte: pressePapiers }); toile.current?.focus(); }}>
              Taper sur le poste
            </button>
            <button className={btn} disabled={!pressePapiers} onClick={() => envoyer({ t: 'presse_papiers_envoyer', texte: pressePapiers })}>
              Copier dans le presse-papiers du poste
            </button>
            <button className={btn} onClick={() => envoyer({ t: 'presse_papiers_lire' })}>
              Lire le presse-papiers du poste
            </button>
            <button className={btn} disabled={!pressePapiers} onClick={() => navigator.clipboard?.writeText(pressePapiers).catch(() => {})}>
              Copier ici
            </button>
            <button className={btn} onClick={() => setPressePapiers(null)}>Fermer</button>
          </div>
        </div>
      )}
      <div
        ref={zone}
        className={`flex-1 min-h-0 min-w-0 bg-black relative flex ${ajuster ? 'overflow-hidden items-center justify-center' : 'overflow-auto'}`}
      >
        <div className="relative inline-block">
          <canvas
            ref={toile}
            tabIndex={0}
            width={0}
            height={0}
            className={`block outline-none ${sourisDispo && sourisSeparee ? 'cursor-default' : 'cursor-none'}`}
            style={ajuster ? tailleAjustee(taille, boite, echelle) : undefined}
            onMouseMove={(e) => souris('deplacer', e)}
            onMouseDown={(e) => souris('appuyer', e)}
            onMouseUp={(e) => souris('relacher', e)}
            onContextMenu={(e) => e.preventDefault()}
          />
          <div
            ref={curseur}
            className="absolute pointer-events-none"
            // Pointeur standard (flèche Windows, pointe au point exact) : le pointeur du poste.
            style={{ display: 'none', width: 13, height: 20, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.45))' }}
          >
            <svg width="13" height="20" viewBox="0 0 13 20" aria-hidden="true">
              <path d="M0.5 0.5 L0.5 16.5 L4.5 12.7 L7.3 19.2 L9.8 18.1 L7.1 11.8 L12.3 11.8 Z" fill="white" stroke="black" strokeWidth="1" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
        {!taille && etat !== 'ferme' && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted pointer-events-none">
            En attente de l’image du poste…
          </div>
        )}
      </div>
    </div>
  );
}
